import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OVERVIEW_URL } from '../app-urls';
import { CURRENT_STATE_PROJECT_KEY } from '../common/linqset-model';
import { DEFAULT_CURRENCY, getCurrency, Organisation } from '../common/organisation';
import { LENSES, LensKey } from '../lenses/lens';
import { FormattingService } from '../linq-utils/formatting/formatting.service';
import { Headline, OverviewService } from './overview.service';
import { LEVER_LABELS, previewRecommendations, Recommendation } from './recommendations';
import { ScopeService, wholeOrganisation } from './scope';
import { ContextGraphButtonComponent } from './context-graph-button.component';

// The analysis overview on the organisation home: the headline figures for the workspace and the first things to do,
// each opening the view that explains it. The same figures and list as the Overview view, for the whole workspace, and
// for a project, beside the current state's
@Component({
    selector: 'app-overview-card',
    standalone: true,
    imports: [MatButtonModule, MatCardModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule, ContextGraphButtonComponent],
    template: `
        <mat-card appearance="raised" data-testid="overview-card">
            <div class="controls-container">
                <h3>Insights overview</h3>
                <mat-card-actions>
                    <button mat-button color="accent" matTooltip="The full overview, with every recommendation and how each model is doing" (click)="openOverview()" data-testid="overview-card-open">Open overview</button>
                </mat-card-actions>
            </div>
            <mat-card-content>
                @if (loading) {
                <div class="status" role="status"><mat-spinner diameter="22"></mat-spinner><span>Working out the overview…</span></div>
                } @else if (error) {
                <p class="status error" role="alert">{{ error }}</p>
                } @else {
                <div class="tiles" aria-label="Headline figures">
                    @for (h of headlines; track h.label) {
                    <button type="button" [class]="'tile ' + h.tone" (click)="openLens(h.lens)" [matTooltip]="'See it in ' + h.lens">
                        <span class="label">{{ h.label }}</span>
                        <strong [matTooltip]="h.detail">{{ h.value }}</strong>
                        @if (h.change) {<span class="vs">@if (h.before) {Current state {{ h.before }}}<span [class]="'chg ' + h.change.tone">{{ h.change.text }}</span></span>}
                    </button>
                    }
                </div>
                @if (compared) {<p class="compared">Each figure is compared with the current state: green is an improvement, red a decline.</p>}
                @if (recommendations.length) {
                <h4>What to do first</h4>
                <ol class="recommendations" data-testid="overview-card-recommendations">
                    @for (r of recommendations; track r.key) {
                    <li [class.urgent]="r.urgent">
                        <span class="priority" [matTooltip]="r.why">{{ r.priority }}</span>
                        <div class="what">
                            <div class="chips"><span [class]="'lever ' + r.lever">{{ leverLabels[r.lever] }}</span>@if (r.urgent) {<span class="now">Act now</span>}</div>
                            <strong class="with-context">{{ r.title }} @if (r.lever !== 'data' && organisation) {<app-context-graph-button [organisation]="organisation" [scope]="scope" [request]="{recommendation: r}" [name]="r.title"></app-context-graph-button>}</strong>
                        </div>
                        <button mat-stroked-button color="accent" (click)="openRecommendation(r)">See in {{ r.lens }}</button>
                    </li>
                    }
                </ol>
                @if (more > 0) {<p class="more"><button mat-button color="accent" (click)="openOverview()">{{ more }} more in the overview</button></p>}
                } @else {
                <p class="none">Nothing stands out: roles have the capacity for their work and information moves between systems automatically.</p>
                }
                }
            </mat-card-content>
        </mat-card>
    `,
    styles: [`
        :host { display: block; }
        mat-card { margin: 5px; }
        /* the title and its action inset as far as the content, as on the other home cards */
        .controls-container { display: flex; align-items: center; justify-content: space-between; padding: 0 16px; }
        .controls-container .mat-mdc-card-actions { padding-right: 0; }
        .mat-mdc-card-content { padding-bottom: 20px; }
        .status { display: flex; align-items: center; gap: 10px; margin: 8px 0; color: var(--slate, #5b6172); font-size: 13px; }
        .error { color: #9b1c1c; }
        .tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin: 4px 0 8px; }
        .tile { display: flex; flex-direction: column; gap: 2px; padding: 10px 12px; border: 1px solid var(--line, #e5e7eb); border-left-width: 4px; border-radius: 10px;
            background: #fff; text-align: left; font: inherit; color: inherit; cursor: pointer; min-width: 0; }
        .tile:hover { box-shadow: 0 2px 8px rgba(15, 23, 42, .12); }
        .tile .label, .tile .sub { font-size: 12px; color: var(--slate, #5b6172); }
        .tile strong { font-size: 20px; font-weight: 600; overflow-wrap: anywhere; }
        .vs { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin-top: 4px; font-size: 11.5px; color: var(--slate, #5b6172); }
        .chg { font-size: 11px; font-weight: 700; white-space: nowrap; padding: 1px 6px; border-radius: 6px; }
        .chg.good { background: #e4f6ec; color: #1e7b45; } .chg.bad { background: #fbe4e2; color: #b42318; } .chg.flat { background: #eef0f6; color: #6b7280; }
        .compared { margin: 0 0 8px; font-size: 12px; color: var(--slate, #5b6172); }
        .tile.bad { border-left-color: #e34948; } .tile.warn { border-left-color: #fab219; } .tile.good { border-left-color: #0ca30c; } .tile.neutral { border-left-color: #94a3b8; }
        h4 { margin: 12px 0 6px; font-size: 14px; }
        .recommendations { list-style: none; margin: 0; padding: 0; display: grid; gap: 6px; }
        .recommendations > li { display: grid; grid-template-columns: 40px minmax(0, 1fr) auto; gap: 12px; align-items: center; padding: 8px 12px; border: 1px solid var(--line, #e5e7eb); border-radius: 8px; }
        .recommendations > li.urgent { border-left: 4px solid #e34948; }
        .priority { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 50%; background: #eef2f7; font-weight: 600; font-variant-numeric: tabular-nums; }
        .chips { display: flex; gap: 6px; font-size: 11px; margin-bottom: 2px; }
        .lever { padding: 1px 8px; border-radius: 10px; background: #eef2f7; color: #334155; }
        .lever.capacity { background: #fde8e8; color: #9b1c1c; } .lever.automate { background: #dcf1f1; color: #0b5e5f; } .lever.integrate { background: #fff1e6; color: #9a3412; }
        .lever.eliminate { background: #fef3c7; color: #92400e; } .lever.protect { background: #e0ecfb; color: #1e40af; } .lever.value { background: #e3f5e3; color: #166534; } .lever.service { background: #efe8fa; color: #5b3d99; }
        .now { font-weight: 600; text-transform: uppercase; letter-spacing: .04em; color: #9b1c1c; }
        .what strong { display: block; font-size: 13px; overflow-wrap: anywhere; }
        .what strong.with-context { display: flex; align-items: center; gap: 2px; }
        .size { font-size: 12px; color: var(--slate, #5b6172); }
        .more { margin: 4px 0 0; }
        .none { margin: 8px 0; color: var(--slate, #5b6172); font-size: 13px; }
        @container card-container (max-width: 700px) { .recommendations > li { grid-template-columns: 40px minmax(0, 1fr); } .recommendations > li button { grid-column: 2; justify-self: start; } }
    `],
})
export class OverviewCardComponent implements OnChanges {
    @Input() organisation?: Organisation;
    @Input() workspaceKey: string = CURRENT_STATE_PROJECT_KEY;
    @Input() trigger: any;              // the data was reloaded

    readonly leverLabels = LEVER_LABELS;
    loading = true;
    error = '';
    compared = false;           // the figures are shown beside the current state's
    headlines: Headline[] = [];
    recommendations: Recommendation[] = [];
    more = 0;
    private request = 0;

    constructor(private overviewService: OverviewService, private scopeService: ScopeService, private router: Router, private formatter: FormattingService) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['organisation'] || changes['workspaceKey'] || changes['trigger']) void this.load();
    }

    private async load() {
        const organisation = this.organisation;
        if (!organisation) return;
        const request = ++this.request;
        this.loading = true;
        this.error = '';
        try {
            // a project is set beside the current state, so each figure shows how it changes
            const comparing = this.scope.workspaceKey !== CURRENT_STATE_PROJECT_KEY;
            const [data, currentState] = await Promise.all([
                this.overviewService.load(organisation, this.scope),
                comparing ? this.overviewService.load(organisation, wholeOrganisation(CURRENT_STATE_PROJECT_KEY)).catch(err => {
                    console.error('Unable to load the current state to compare with', err);
                    return undefined;
                }) : undefined,
            ]);
            if (request !== this.request) return;
            if (currentState) this.overviewService.compare(data, currentState, organisation);
            this.compared = !!currentState;
            this.headlines = data.headlines;
            // at most two to act on now, so the largest of the rest show too
            this.recommendations = previewRecommendations(data.recommendations, 5, 2);
            this.more = Math.max(0, data.recommendations.length - this.recommendations.length);
        } catch (err) {
            if (request !== this.request) return;
            console.error('Unable to build the overview', err);
            this.error = "The overview couldn't be worked out. Please try again.";
        } finally {
            if (request === this.request) this.loading = false;
        }
    }

    // the analysis views look at this workspace, whole
    private setScope() {
        if (this.organisation) this.scopeService.open(this.organisation._key, wholeOrganisation(this.workspaceKey || CURRENT_STATE_PROJECT_KEY));
    }

    // the whole of this workspace, as the card looks at
    get scope() {
        return wholeOrganisation(this.workspaceKey || CURRENT_STATE_PROJECT_KEY);
    }

    openOverview() {
        if (!this.organisation) return;
        this.setScope();
        void this.router.navigateByUrl(OVERVIEW_URL(this.organisation._key));
    }

    openLens(key: LensKey) {
        const lens = LENSES.find(l => l.key === key);
        if (!lens || !this.organisation) return;
        this.setScope();
        void this.router.navigateByUrl(lens.url(this.organisation._key));
    }

    openRecommendation(r: Recommendation) {
        const lens = LENSES.find(l => l.key === r.lens);
        if (!lens || !this.organisation) return;
        this.setScope();
        void this.router.navigate([lens.url(this.organisation._key)], r.focus ? { queryParams: { focus: r.focus } } : {});
    }

    hours(value: number) {
        return this.formatter.formatAbbreviatedNumber(value);
    }

    money(value: number) {
        return this.formatter.formatAbbreviatedNumber(value, getCurrency(this.organisation?.currencyCode || DEFAULT_CURRENCY));
    }
}
